import { type AnyGraphNode, nodeName } from '../types/graphNodes';
import type { FilterDefinition, FilterValue, EdgeFilterValue } from '../filters/types';
import { nameFilter, nodeIdFilter, edgeFilter, flagFilter } from '../filters/definitions';

/**
 * Every edge a /browse card can pre-filter on needs an entry here, or its pill
 * renders the raw edge name (a "westernNotes" pill, verbatim).
 */
const EDGE_LABELS: Record<string, string> = {
  deity: 'Deity', ragam: 'Ragam', talam: 'Talam', composer: 'Composer',
  westernNotes: 'Western Notes',
};

/**
 * Filter definitions for the bhajan browser. Includes the shared `edge`
 * definition (so facet filters applied via Browse drill-down execute and render
 * as pills), but with a display value that resolves target nodeIds to names —
 * e.g. "Deity: Ganapati" instead of "Deity #600".
 *
 * `flag` is here for the same reason: a filterId with no definition is passed
 * through by executeFilterSet (filterExecutor.ts:45-48), so a /browse card
 * linking to an unregistered filter would quietly show the WHOLE library.
 */
export function makeBhajanFilterDefinitions(allNodes: AnyGraphNode[]): Record<string, FilterDefinition> {
  const byId = new Map(allNodes.map((n) => [n.nodeId, n] as const));
  const edgeWithNames: FilterDefinition = {
    ...edgeFilter,
    getDisplayValue(value: FilterValue): string {
      const v = value as EdgeFilterValue;
      const label = EDGE_LABELS[v?.edgeName] ?? v?.edgeName ?? 'Filter';
      const ids = v?.targetNodeIds ?? [];
      if (ids.length === 1) {
        const name = nodeName(byId.get(ids[0]) as AnyGraphNode | undefined);
        return name ? `${label}: ${name}` : label;
      }
      if (ids.length > 1) return `${label} (${ids.length})`;
      return label;
    },
  };
  return { name: nameFilter, nodeId: nodeIdFilter, edge: edgeWithNames, flag: flagFilter };
}
